import React, { Component } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FormControl, InputLabel, Select, MenuItem, TextField, Paper, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { fetchPatientWithQuery } from '../../../../services/clinicPortalServices/patientSearchService';
import { getUserRole } from '../../../../services/common/util';

const SearchMenuContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    '& .form-group': {
        marginBottom: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    '& .form-label': {
        marginBottom: theme.spacing(1),
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.palette.text.secondary
    }
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: '40px',
    '& .btn': {
        height: '36px',
        minHeight: '36px',
        lineHeight: '1.25',
        padding: '6px 12px',
        fontSize: '0.875rem',
        display: 'inline-flex',
        alignItems: 'center',
        '& i': {
            fontSize: '0.875rem'
        }
    }
}));

export default class PatientMedicationSearchMenu extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedPatient: null,
            patientOptions: [],
            isLoadingPatients: false,
            searchText: ''
        };
    }

    componentDidMount() {
        // If we have a patientId prop, load that patient
        if (this.props.patientId) {
            this.loadPatientById(this.props.patientId);
        }
    }

    loadPatientById = (patientId) => {
        fetchPatientWithQuery({ _id: patientId })
            .then((response) => {
                if (response.data && response.data.length > 0) {
                    const patient = response.data[0];
                    const patientOption = {
                        value: patient._id,
                        label: `${patient.first_name} ${patient.last_name} (MRN: ${patient.mrn})`
                    };
                    this.setState({ 
                        selectedPatient: patientOption,
                        patientOptions: [patientOption]
                    });
                }
            })
            .catch((error) => {
                console.error('Error loading patient:', error);
            });
    };

    handlePatientSearch = (inputValue) => {
        if (!inputValue || inputValue.length < 2) return;
        
        this.setState({ isLoadingPatients: true });
        
        const searchQuery = {
            $or: [
                { first_name: { $regex: inputValue, $options: 'i' } },
                { last_name: { $regex: inputValue, $options: 'i' } },
                { mrn: { $regex: inputValue, $options: 'i' } }
            ]
        };
        
        fetchPatientWithQuery(searchQuery)
            .then((response) => {
                const options = response.data.map(patient => ({
                    value: patient._id,
                    label: `${patient.first_name} ${patient.last_name} (MRN: ${patient.mrn})`
                }));
                this.setState({ 
                    patientOptions: options,
                    isLoadingPatients: false 
                });
            })
            .catch((error) => {
                console.error('Error searching patients:', error);
                this.setState({ isLoadingPatients: false });
            });
    };

    handlePatientChange = (selectedOption) => {
        this.setState({ selectedPatient: selectedOption });
        if (selectedOption && this.props.onPatientSelected) {
            this.props.onPatientSelected(selectedOption.value);
        }
    };

    handleQuickFilterChange = (e) => {
        const value = e.target.value;
        this.setState({ searchText: value });
        this.props.onQuickFilterChanged(value);
    };

    handleClearFilter = () => {
        this.setState({ searchText: '' });
        this.props.onClearFilter();
    };

    render() {
        const userRole = getUserRole();
        const canExport = !/facilityadmin|facilityuser/.test(userRole.toLowerCase());
        const canEdit = !/audit|salesperson/.test(userRole.toLowerCase());
        const showPatientSearch = !this.props.patientId;

        return (
            <SearchMenuContainer elevation={1}>
                <div className="row align-items-stretch">
                    {showPatientSearch && (
                        <div className="col-lg-3 col-md-6 col-sm-12 mb-2">
                            <Form.Group>
                                <Form.Label>Patient</Form.Label>
                                <TextField
                                    fullWidth
                                    placeholder="Search by name or MRN..."
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value.length >= 2) {
                                            this.handlePatientSearch(value);
                                        }
                                    }}
                                    select={this.state.patientOptions.length > 0}
                                    value={this.state.selectedPatient?.value || ''}
                                    SelectProps={{
                                        onChange: (e) => {
                                            const selectedId = e.target.value;
                                            const selectedOption = this.state.patientOptions.find(opt => opt.value === selectedId);
                                            this.handlePatientChange(selectedOption);
                                        }
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Search for patient...</em>
                                    </MenuItem>
                                    {this.state.patientOptions.map(patient => (
                                        <MenuItem key={patient.value} value={patient.value}>
                                            {patient.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Form.Group>
                        </div>
                    )}
                    
                    <div className="col-lg-3 col-md-4 col-sm-12 mb-2">
                        <Form.Group>
                            <Form.Label>Quick Filter</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Search medications..."
                                value={this.state.searchText}
                                onChange={this.handleQuickFilterChange}
                            />
                        </Form.Group>
                    </div>
                    
                    <div className="col-lg-2 col-md-2 col-sm-12 mb-2">
                        <Form.Group>
                            <Form.Label>Page Size</Form.Label>
                            <Form.Control 
                                type="number"
                                min="1"
                                max="1000"
                                value={this.props.pageSize}
                                onChange={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (value > 0 && value <= 1000) {
                                        this.props.onPageSizeChanged(value);
                                    }
                                }}
                                onBlur={(e) => {
                                    const value = parseInt(e.target.value, 10);
                                    if (!value || value < 1) {
                                        this.props.onPageSizeChanged(20);
                                    } else if (value > 1000) {
                                        this.props.onPageSizeChanged(1000);
                                    }
                                }}
                                placeholder="Page size"
                            />
                        </Form.Group>
                    </div>
                    
                    <div className="col-lg-4 col-md-12 col-sm-12 mb-2">
                        <Form.Group>
                            <Form.Label>Actions</Form.Label>
                            <ButtonGroup>
                            {canEdit && (
                                <Button
                                    variant="success"
                                    onClick={this.props.onAddMedication}
                                    disabled={!this.props.patientId && !this.state.selectedPatient}
                                >
                                    <i className="fas fa-plus mr-2"></i>
                                    Add Medication
                                </Button>
                            )}
                            
                            <Button 
                                variant="secondary"
                                onClick={this.handleClearFilter}
                                disabled={!this.state.searchText}
                            >
                                <i className="fas fa-times mr-2"></i>
                                Clear Filter
                            </Button>
                            
                            {canExport && (
                                <Button 
                                    variant="primary"
                                    onClick={this.props.onExport}
                                >
                                    <i className="fas fa-download mr-2"></i>
                                    Export
                                </Button>
                            )}
                            </ButtonGroup>
                        </Form.Group>
                    </div>
                </div>
            </SearchMenuContainer>
        );
    }
}